import type { CreateInvoiceRequest } from "@/services/invoiceService";
import type { InvoiceLine, TaxCategory } from "@fiscalo/shared-types";

export const SERIES = "A";
const CONSUMER_RUT = "";
const EXPORT_TAX_CATEGORY: TaxCategory = "exempt";

/** CFE NomItem max length (DGI). */
const NOM_ITEM_MAX = 80;

export type CfeType = "101" | "111" | "121";

export interface LineForm {
  description: string;
  quantity: number;
  unitPrice: number;
  taxCategory: TaxCategory;
  /**
   * Which tax option produced taxAmount. Persisted as `tax.rate` on the line,
   * not as an id: category + rate identifies the option unambiguously, and
   * TaxInfo already had an optional rate so nothing new is stored.
   *
   * It matters because `vat_other` alone cannot say whether a line is the
   * 2,2% automotor rate or the 2,75% moto one.
   */
  taxRate?: number;
  taxAmount: number;
  /** When set, the line matched a client catalog item (for picker display). */
  catalogItemId?: string | null;
  /** Additional line text (CFE DscItem); optional. */
  dscItem?: string;
}

export interface ReceiverForm {
  name: string;
  rut: string;
  address: string;
  countryCode: string;
  docRecepExt: string;
  ciudad: string;
  departamento: string;
}

export function normalizeRut(rut: string): string {
  return rut.replace(/\D/g, "").padStart(12, "0").slice(0, 12);
}

/**
 * True when the line already ends with the tenant exonerated-regime legend (same text as stored after a prior emission).
 * In that case {@link buildPayload} must not append again—reissue should keep the same line text.
 */
function lineAlreadyEndsWithRegimeLegend(description: string, legend: string): boolean {
  const L = legend.trim();
  if (!L) return false;
  const d = description.replace(/\r\n/g, "\n").trimEnd();
  if (d === L) return true;
  return d.endsWith("\n" + L);
}

export function buildPayload(
  cfeType: CfeType,
  receiver: ReceiverForm,
  issueDate: string,
  linesForm: LineForm[],
  lineCurrency: "UYU" | "USD",
  exchangeRate: number,
  exoneratedRegimeLegend?: string | null,
  paymentMethod: "contado" | "credito" = "contado",
  /**
   * The adenda (CFE Zona J) for this invoice.
   *
   * An OPTIONS OBJECT rather than two more positional arguments: this function
   * already takes eight, and the next caller to append a boolean in the wrong
   * slot would silently suppress every tenant's standing adenda.
   */
  options?: {
    /** This invoice's own adenda text, APPENDED to the standing one. */
    adendaText?: string | null;
    /** Drop the standing (marca/company) adenda for this invoice only. */
    adendaSuppressDefault?: boolean;
  }
): CreateInvoiceRequest {
  const currency = cfeType === "121" ? "USD" : lineCurrency;
  const toInvoiceCurrency = 1;
  const rut =
    cfeType === "111"
      ? normalizeRut(receiver.rut)
      : cfeType === "101"
        ? CONSUMER_RUT
        : CONSUMER_RUT;

  const regimeSuffix = exoneratedRegimeLegend?.trim();

  const lines: InvoiceLine[] = linesForm.map((line, index) => {
    const lineNumber = index + 1;
    const netUnit = line.unitPrice * toInvoiceCurrency;
    const subtotal = line.quantity * netUnit;
    const category: TaxCategory =
      cfeType === "121" ? EXPORT_TAX_CATEGORY : line.taxCategory;
    const taxAmount =
      cfeType === "121" ? 0 : line.taxAmount * toInvoiceCurrency;
    const lineTotalAmount = subtotal + taxAmount;
    const normalizedDesc = line.description.replace(/\r\n/g, "\n").trim();
    const manualDsc = line.dscItem?.trim();

    let description: string;
    let dscItemOut: string | undefined;

    if (regimeSuffix && !lineAlreadyEndsWithRegimeLegend(normalizedDesc, regimeSuffix)) {
      description = normalizedDesc.slice(0, NOM_ITEM_MAX);
      dscItemOut = regimeSuffix;
    } else {
      description = normalizedDesc;
      dscItemOut = manualDsc ? manualDsc.slice(0, 1000) : undefined;
    }

    return {
      lineNumber,
      description,
      ...(dscItemOut !== undefined ? { dscItem: dscItemOut } : {}),
      quantity: line.quantity,
      unitPrice: { currency, amount: Math.round(netUnit * 100) / 100 },
      tax: {
        category,
        // Exports are forced exempt above, so their rate is 0 regardless of
        // what the line was edited as.
        ...(cfeType === "121"
          ? {}
          : line.taxRate != null
            ? { rate: line.taxRate }
            : {}),
        amount: Math.round(taxAmount * 100) / 100,
      },
      lineTotal: {
        currency,
        amount: Math.round(lineTotalAmount * 100) / 100,
      },
    };
  });

  const subtotalAmount = linesForm.reduce(
    (sum, l) => sum + l.quantity * l.unitPrice * toInvoiceCurrency,
    0
  );
  const taxTotalForPayload =
    cfeType === "121"
      ? 0
      : linesForm.reduce(
          (sum, l) => sum + l.taxAmount * toInvoiceCurrency,
          0
        );
  const totalAmount = subtotalAmount + taxTotalForPayload;

  let receiverSnapshot: CreateInvoiceRequest["receiverSnapshot"];
  if (cfeType === "101") {
    // An e-Ticket CAN identify its receptor (required above the Consumidor
    // Final tope): a Cédula (≤8 digits → TipoDocRecep=3) or a RUT. Send the
    // typed doc UNPADDED so the backend/DGI mapper detects a CI by length;
    // omit it for an anonymous Consumidor Final or the all-zeros placeholder.
    const receptorDoc = (receiver.rut ?? "").replace(/\D/g, "");
    const identifiesReceptor = receptorDoc.length > 0 && !/^0+$/.test(receptorDoc);
    receiverSnapshot = {
      ...(identifiesReceptor ? { rut: receptorDoc } : {}),
      ...(receiver.name.trim() ? { name: receiver.name.trim() } : {}),
      ...(receiver.address.trim() ? { address: receiver.address } : {}),
      ...(receiver.countryCode?.trim() && receiver.countryCode !== "OTHER"
        ? { countryCode: receiver.countryCode.slice(0, 2).toUpperCase() }
        : {}),
      ...(receiver.docRecepExt?.trim()
        ? { docRecepExt: receiver.docRecepExt.slice(0, 20) }
        : {}),
    };
  } else if (cfeType === "111") {
    const countryCode =
      receiver.countryCode && receiver.countryCode !== "OTHER"
        ? receiver.countryCode.slice(0, 2).toUpperCase()
        : "UY";
    receiverSnapshot = {
      rut,
      name: receiver.name.trim(),
      countryCode,
      ...(receiver.address.trim() ? { address: receiver.address.trim() } : {}),
      ...(receiver.ciudad.trim() ? { ciudad: receiver.ciudad.trim() } : {}),
      ...(receiver.departamento.trim() ? { departamento: receiver.departamento.trim() } : {}),
    };
  } else {
    receiverSnapshot = {
      name: receiver.name.trim(),
      countryCode:
        receiver.countryCode && receiver.countryCode !== "OTHER"
          ? receiver.countryCode
          : undefined,
      ...(receiver.docRecepExt.trim()
        ? { docRecepExt: receiver.docRecepExt.slice(0, 20) }
        : {}),
    };
  }

  const payload: CreateInvoiceRequest = {
    cfeType,
    series: SERIES,
    currency,
    issueDate,
    receiverSnapshot,
    lines,
    paymentMethod,
    totals: {
      subtotal: {
        currency,
        amount: Math.round(subtotalAmount * 100) / 100,
      },
      taxTotal: {
        currency,
        amount: Math.round(taxTotalForPayload * 100) / 100,
      },
      total: {
        currency,
        amount: Math.round(totalAmount * 100) / 100,
      },
    },
  };

  if (cfeType === "121") {
    payload.exportHeader = { clauVenta: "FOB", viaTransp: 1 };
  }

  if (currency === "USD" && (cfeType === "101" || cfeType === "111")) {
    payload.exchangeRate = exchangeRate;
  }

  // Omit when UNDEFINED, not when falsy. Omitting an empty string made it
  // impossible to CLEAR an adenda on a draft update: the key never went, so
  // the stored text survived and the user had no way to remove it. A caller
  // that passed the field means it, including when it is empty.
  if (options?.adendaText !== undefined) {
    payload.adendaText = options.adendaText?.trim() ?? "";
  }
  if (options?.adendaSuppressDefault !== undefined) {
    payload.adendaSuppressDefault = options.adendaSuppressDefault;
  }

  return payload;
}
